import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useDisplayKey } from "@/lib/dateTime";
import { Banners } from "@/components/shell/Banners";
import { CommandPalette } from "@/components/shell/CommandPalette";
import { IconRail } from "@/components/shell/IconRail";
import { SectionPanel } from "@/components/shell/SectionPanel";
import { ShellProvider, useShell } from "@/components/shell/ShellContext";
import { TopBar } from "@/components/shell/TopBar";
import { useNav } from "@/components/shell/nav";
import { SoftphoneProvider } from "@/components/calling/SoftphoneContext";
import { PushListener } from "@/components/push/PushListener";
import { KeyboardShortcuts } from "@/components/shell/KeyboardShortcuts";
import { RecentTracker } from "@/components/recent/RecentTracker";
import { PreviewProvider } from "@/components/preview/PreviewContext";
import { PushPrompt } from "@/components/push/PushPrompt";
import { VisitBar } from "@/components/visits/VisitBar";
import { AskPanel } from "@/components/ai/AskPanel";
import { AskPanelProvider } from "@/components/ai/AskPanelContext";

/**
 * App frame: navy icon rail, an optional views panel, and the page.
 * Pages put their own views in the panel with <ShellPanel>; Settings and Users & roles
 * get theirs from the navigation. On phones the rail and panel slide in as one drawer.
 */
function Frame({ nav }: { nav: ReturnType<typeof useNav> }) {
  const { pathname } = useLocation();
  const displayKey = useDisplayKey();
  const { setPanelEl, panelCount, viewsHidden } = useShell();
  const [drawer, setDrawer] = useState(false);

  // Close the drawer after navigating
  useEffect(() => setDrawer(false), [pathname]);

  const hasPanel = panelCount > 0;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
      {drawer && <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setDrawer(false)} aria-hidden="true" />}

      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex shadow-xl transition-transform duration-200 md:static md:z-auto md:translate-none md:shadow-none",
          drawer ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <IconRail nav={nav} />
        <aside
          ref={setPanelEl}
          aria-label="Views"
          // Shown unless hidden with the top bar's button (always in the phone menu)
          className={cn("w-[220px] shrink-0 overflow-y-auto border-r border-border bg-panel", !hasPanel ? "hidden" : viewsHidden ? "block md:hidden" : "block")}
        />
      </div>

      <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
        <Banners />
        <PushPrompt />
        <TopBar onOpenMenu={() => setDrawer(true)} />
        {/* A container: pages lay themselves out by the room they have (less while Ask AI sits beside them) */}
        <main className="@container/main min-h-0 flex-1 overflow-y-auto bg-background p-4 md:p-6">
          <Outlet key={displayKey} />
        </main>
        <VisitBar />
      </div>

      <AskPanel />

      {nav.panel && <SectionPanel title={nav.panel.title} sections={nav.panel.sections} />}
    </div>
  );
}

export function AppLayout() {
  const nav = useNav();

  return (
    <ShellProvider>
      <TooltipProvider delayDuration={150}>
        <SoftphoneProvider>
          <PushListener />
          <RecentTracker />
          <KeyboardShortcuts />
          <AskPanelProvider>
            <CommandPalette groups={nav.groups} admin={nav.admin} settingsSections={nav.settingsPanel} usersSections={nav.usersPanel}>
              <PreviewProvider>
                <Frame nav={nav} />
              </PreviewProvider>
            </CommandPalette>
          </AskPanelProvider>
        </SoftphoneProvider>
      </TooltipProvider>
    </ShellProvider>
  );
}
